import { useRef, useState } from "react";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { SecondaryAction } from "./kit";
import api, { mediaUrl } from "../lib/api";
import { resizePhotoToDataUrl } from "../lib/profilePhoto";
import { resolvePhotoUploadErrorMessage, resolvePhotoRemoveErrorMessage } from "../lib/settingsErrorMessages";
import { useAuth } from "../context/AuthContext";
import { OUTLINE_ACTION_STYLE, AlertCallout } from "./SettingsSignOut";

/**
 * The photo control inside the Edit your details sheet (#1135, R4). One photo
 * per person: a player's goes through `POST/DELETE /players/me/photo` and a
 * coach's through `POST/DELETE /coach-profiles/me/photo`, the endpoints the
 * shipped profile forms already use (`pages/PlayerProfile.jsx`,
 * `CoachProfileCard.jsx`). A photo uploads the moment it is picked, not on the
 * sheet's Save, exactly as today; `updateUser` pushes the stored value into
 * the auth context so the bar's avatar repaints.
 *
 * The testids are the role's shipped ones, so the journeys that drove them
 * keep their handles: `pp-photo-*` for a player, `coach-profile-photo-*` for a
 * coach. A failure shows here on the page (D57); a success toasts.
 */
const ALERT = "var(--nw-alert)";

export default function SettingsPhotoControl() {
  const { user, updateUser } = useAuth();
  const isCoach = user?.role === "coach";
  const stem = isCoach ? "coach-profile-photo" : "pp-photo";
  const base = isCoach ? "/coach-profiles/me/photo" : "/players/me/photo";
  const field = isCoach ? "photo" : "profile_photo";
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);
  const photo = user?.profile_photo || null;

  const apply = (next) => updateUser({ ...user, profile_photo: next });

  const change = async (file) => {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const resized = await resizePhotoToDataUrl(file);
      const { data } = await api.post(base, { photo: resized });
      apply(data[field]);
      toast.success("Photo updated");
    } catch (e) {
      setError(resolvePhotoUploadErrorMessage(e));
    }
    setBusy(false);
  };

  const remove = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.delete(base);
      apply(data[field] ?? null);
      toast.success("Photo removed");
    } catch (e) {
      setError(resolvePhotoRemoveErrorMessage(e));
    }
    setBusy(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }} data-testid="settings-photo-control">
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <Avatar className="h-14 w-14 shrink-0 border-2" style={{ borderColor: "var(--nw-line-strong)" }}>
          {photo && <AvatarImage src={mediaUrl(photo)} alt="" />}
          <AvatarFallback className="text-sm font-semibold">{(user?.display_name || "?").trim().slice(0, 1).toUpperCase()}</AvatarFallback>
        </Avatar>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          data-testid={`${stem}-input`}
          onChange={(e) => {
            change(e.target.files?.[0]);
            // The same file picked twice must fire onChange twice.
            e.target.value = "";
          }}
        />
        <SecondaryAction
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          data-testid={`${stem}-change`}
          style={{ ...OUTLINE_ACTION_STYLE, opacity: busy ? 0.6 : 1 }}
        >
          {busy ? "Working…" : "Change photo"}
        </SecondaryAction>
        {photo && (
          <SecondaryAction
            onClick={remove}
            disabled={busy}
            data-testid={`${stem}-remove`}
            style={{ ...OUTLINE_ACTION_STYLE, border: `1px solid ${ALERT}`, color: ALERT, opacity: busy ? 0.6 : 1 }}
          >
            Remove photo
          </SecondaryAction>
        )}
      </div>
      {error && <AlertCallout testId={`${stem}-error`}>{error}</AlertCallout>}
    </div>
  );
}
